import { HttpClient } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CwAvatar, CwIcon, CwMenu, CwMenuItem } from '@bbraun/cortex-angular';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, combineLatest, from, of, switchMap, take } from 'rxjs';
import { IconOrganizationSettingsComponent } from '~/app/custom-icons/icon-organization-settings/icon-organization-settings.component';
import { CustomShadowCssDirective } from '~/app/directives/CustomShadowCss';
import { LastContext, Site, User } from '~/app/models/User';
import { AuthService } from '~/app/services/auth.service';
import { UserService } from '~/app/services/user.service';
import { environment } from '~/environments/environment';

@Component({
  selector: 'gplus-user',
  imports: [
    CwAvatar,
    CwMenu,
    CwMenuItem,
    TranslateModule,
    CustomShadowCssDirective,
    CwIcon,
    IconOrganizationSettingsComponent
  ],
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss'
})
export class UserComponent {
  public readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  public profile = signal<User>(null);
  public image = signal<string | null>(null);
  public isAdmin = signal<boolean>(false);

  public sites = signal<Array<Site>>([]);

  constructor() {
    this.userService
      .getMe()
      .pipe(
        take(1),
        switchMap((me) => {
          this.profile.set(me);

          const userUnitId = this.userService?.context()?.unitId;
          this.sites.set(
            me?.sites
              ?.toSorted((a, b) => a.name.localeCompare(b.name))
              .map((site) => ({
                ...site,
                units: site?.units?.toSorted((a, b) => {
                  if (a.id === userUnitId) return -1;
                  if (b.id === userUnitId) return 1;
                  return a.name.localeCompare(b.name);
                })
              }))
          );

          return combineLatest([this.getImage(this.profile().avatarUrl), this.userService.hasAdminAccess()]);
        })
      )
      .subscribe(([image, hasAdminAccess]) => {
        this.image.set(image);
        this.isAdmin.set(hasAdminAccess);
      });
  }

  private getImage(url: string | null | undefined) {
    if (!url) {
      return of(null);
    }
    return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
      switchMap((response) => {
        const body = response?.body;
        // Fall back to the letters avatar unless the server returned an actual image.
        if (!body || body.size === 0 || !body.type.startsWith('image/')) {
          return of(null);
        }
        // Use a data: URL instead of a blob: URL to satisfy the CSP img-src directive.
        return from(this.blobToDataUrl(body));
      }),
      catchError(() => of(null))
    );
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  public logout() {
    this.authService.logout();
  }

  public toProfilePage() {
    window.open(`${environment.profilePage}`, '_blank');
  }

  public toOrganizationSettings() {
    this.router.navigate(['/organization-settings'], {
      queryParamsHandling: 'merge'
    });
  }

  public selectedContext(context: LastContext): void {
    this.userService.setContext(context).subscribe(() => {
      this.router
        .navigate(['/home'], {
          queryParamsHandling: 'merge'
        })
        .then(() => {
          this.reloadPage();
        });
    });
  }

  // we make this public so we can spy on it in unit test
  public async reloadPage() {
    window.location.reload();
  }
}
